import React, { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import { Form, InputGroup } from "react-bootstrap";
import { useFormikContext } from "formik";
import ReactMarkdown from "react-markdown";
import { Recaptcha } from "../../components/Recaptcha";
import ToggleButtons from "../Buttons/ToggleButtons";
import {
  handleTrackingNumberChange,
  handleChangeWithContext,
  stripHTML,
  isSafeImageSrc
} from "../../utils";
import { useAuth } from "../../components/AuthContext/AuthContext";
import {
  Eye,
  EyeSlash,
  InfoCircleFill,
  ExclamationCircleFill,
} from "react-bootstrap-icons";

import eyeSlash from "../../assets/icons/EyeSlash.svg";
import { formatErrorMessage, getCountryFromPhone, removeCountryCodeForUkNumber  } from "../../utils/utils";
import InputMask from "react-input-mask";
import calenderIcon from "../../assets/icons/calendar-icon.svg";
import Select from "react-select";
import BasicTimePicker from "./TimePickerField";
import BasicDatePicker from "./DatePickerField";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import flags from "react-phone-number-input/flags";
import IntlTelInput, { intlTelInput } from "@intl-tel-input/react";
import "intl-tel-input/styles";

const convert = require("convert-units");

const darkGreen = "#004137";
const PHONE_MAX_LENGTH = 13;

let errorMessage;

const formatAddressOptionLabel = (option, { context }) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    }}
  >
    <span>{option.label}</span>
    {option.value === "No results" && context === "menu" && (
      <span
        style={{
          marginLeft: "8px",
          fontWeight: "bold",
          fontSize: "16px",
          color: "#007bff",
          lineHeight: 1,
        }}
      >
        +
      </span>
    )}
  </div>
);

const BARCODE_CHECK_STRINGS = [
  "blood test kit",
  "barcode",
  "already been activated",
  "has already been",
  "please ensure that you have selected",
  "require a replacement kit",
  "but there's no need to worry",
  "blocked by our quality team",
  "barcode incorrect",
  "there seems to be an issue with your test kit, but there’s no need to worry. please contact care@myhealthchecked.com and quote error code 0605, and we will help you get back on track swiftly.",
  "we regret to inform you that the barcode you have entered has passed its use-by date. please contact our Customer Care Team via care@myhealthchecked.com for further assistance.",
];

const textIncludesBarcodeString = (text) =>
  BARCODE_CHECK_STRINGS.some((str) => text.toLowerCase().includes(str));

const isBarcodeError = (validationError) => {
  if (React.isValidElement(validationError)) {
    return validationError.props.children.some(
      (child) => typeof child === "string" && textIncludesBarcodeString(child),
    );
  }
  if (typeof validationError === "string") {
    return textIncludesBarcodeString(validationError);
  }
  return false;
};

const getGroupCheckboxChecked = (values, name, label) => {
  const groupValue = values[name];
  return Array.isArray(groupValue) && groupValue.includes(label);
};

const GROUP_CHECKBOX_NAMES = new Set([
  "unprotected_sex_type",
  "potential_sti_exposure_type",
  "condition",
  "medications",
  "supplements",
]);

const hasBarcodeValidationError = (barcodeError, props) => {
  return Boolean(
    barcodeError && props.name === "code" && props.validationError,
  );
};

const renderHomeAddressOption = (props) => {
  return props?.name === "home_street_address"
    ? props?.value || props?.placeholder
    : props?.placeholder;
};

const MarkdownComponents = {
  a: ({ node, children, ...props }) => (
    <a
      {...props}
      target="_blank"
      rel="noreferrer"
      aria-label={typeof children === "string" ? children : "External link"}
    >
      {children}
    </a>
  ),
};

function FormInput(props) {
  const { values, touched, errors, setFieldValue, status, setStatus } = useFormikContext();
  const [inputValue, setInputValue] = useState(values[props.name] || "");
  const [password, setPassword] = useState(values[props.name] || "");
  const [showPassword, setShowPassword] = useState(false);
  const auth = useAuth();
  const [mask, setMask] = useState("**********");
  const [phoneValidity, setPhoneValidity] = useState(true);
  const [phoneErrorCode, setPhoneErrorCode] = useState(null);
  const [showPhoneValidation, setShowPhoneValidation] = useState(false);
  // Use refs to track previous values without causing re-renders
  const prevHeightUnitRef = useRef(values.heightUnits);
  const prevWeightUnitRef = useRef(values.weightUnits);
  const isConvertingHeightRef = useRef(false);
  const itiRef = useRef(null);
  const isConvertingWeightRef = useRef(false);
  let cachedCountryPromise = null;
  const getIpCountry = () => {
    const cached = sessionStorage.getItem("ip_country");
    if (cached) return Promise.resolve(cached);

    if (!cachedCountryPromise) {
      cachedCountryPromise = fetch("https://ipwho.is/")
        .then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
        .then((data) => {
          const code = data?.country_code?.toLowerCase() || "gb";
          sessionStorage.setItem("ip_country", code);
          return code;
        })
        .catch((err) => {
          console.error("IP lookup failed:", err);
          return "gb";
        });
    }
    return cachedCountryPromise;
  };
  const [selectedCountry, setSelectedCountry] = useState(null);
  const isConvertingRef = useRef(false);
  // Lets telephoneValidation pick UK vs IntlTelInput rules for this field
  useEffect(() => {
    if (props.type === "phone" || props.type === "advancePhoneNumber") {
      setFieldValue(`${props.name}_type`, props.type, false);
    }
  }, [props.type, props.name]);
  useEffect(() => {
    const raw = values[props.name];
    if (
      props.type === "advancePhoneNumber" &&
      raw?.startsWith("+") &&
      itiRef.current
    ) {
      const iti = itiRef.current.getInstance();
      iti?.setNumber(raw); // triggers internal country detection
    }
  }, []);
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const isNhsResult = props.name === "nhs_results";
  const isNhsNumber = props.name === "nhs_number";

  // Sync inputValue with Formik values
  useEffect(() => {
    const formikValue = values[props.name];
    if (formikValue !== inputValue) {
      setInputValue(formikValue || "");
    }
  }, [values[props.name]]);

  const initialCountryLookup = async () => {
    const res = await fetch("https://ipapi.co/json");
    const data = await res.json();
    // UK is excluded from the non-UK picker, so don't preselect it
    return data.country_code?.toLowerCase() === "gb" ? "" : data.country_code;
  };
  useEffect(() => {
    if (props.label === "informed-consent-with-modal") {
      const link = document.querySelector(".informed-consent-link");
      if (link && props.onInformedConsentClick) {
        link.addEventListener("click", props.onInformedConsentClick);
        return () => {
          link.removeEventListener("click", props.onInformedConsentClick);
        };
      }
    }
  }, [props.label, props.onInformedConsentClick]);

  // Handle height unit conversions
  useEffect(() => {
    const currentHeightUnit = values.heightUnits;
    const prevHeightUnit = prevHeightUnitRef.current;

    if (
      !currentHeightUnit ||
      currentHeightUnit === prevHeightUnit ||
      isConvertingHeightRef.current ||
      !values.height
    ) {
      prevHeightUnitRef.current = currentHeightUnit;
      return;
    }

    const currentHeight = Number.parseFloat(values.height);
    if (Number.isNaN(currentHeight) || currentHeight === 0) {
      prevHeightUnitRef.current = currentHeightUnit;
      return;
    }

    isConvertingHeightRef.current = true;

    let convertedHeight;
    try {
      if (prevHeightUnit === "metric" && currentHeightUnit === "imperial") {
        // Convert cm → in
        convertedHeight = Number(
          convert(currentHeight).from("cm").to("in").toFixed(2),
        );
      } else if (
        prevHeightUnit === "imperial" &&
        currentHeightUnit === "metric"
      ) {
        // Convert in → cm
        convertedHeight = Number(
          convert(currentHeight).from("in").to("cm").toFixed(2),
        );
      }

      if (convertedHeight !== undefined && !Number.isNaN(convertedHeight)) {
        setFieldValue("height", convertedHeight.toString());
      }
    } catch (error) {
      console.error("Height conversion error:", error);
    } finally {
      prevHeightUnitRef.current = currentHeightUnit;
      setTimeout(() => {
        isConvertingHeightRef.current = false;
      }, 100);
    }
  }, [values.heightUnits, values.height, setFieldValue]);

  // Handle weight unit conversions
  useEffect(() => {
    const currentWeightUnit = values.weightUnits;
    const prevWeightUnit = prevWeightUnitRef.current;

    if (
      !currentWeightUnit ||
      currentWeightUnit === prevWeightUnit ||
      isConvertingWeightRef.current ||
      !values.weight
    ) {
      prevWeightUnitRef.current = currentWeightUnit;
      return;
    }

    const currentWeight = Number.parseFloat(values.weight);
    if (Number.isNaN(currentWeight) || currentWeight === 0) {
      prevWeightUnitRef.current = currentWeightUnit;
      return;
    }

    isConvertingWeightRef.current = true;

    let convertedWeight;
    try {
      if (prevWeightUnit === "metric" && currentWeightUnit === "imperial") {
        // kg → lb
        convertedWeight = Number(
          convert(currentWeight).from("kg").to("lb").toFixed(2),
        );
      } else if (
        prevWeightUnit === "imperial" &&
        currentWeightUnit === "metric"
      ) {
        // lb → kg
        convertedWeight = Number(
          convert(currentWeight).from("lb").to("kg").toFixed(2),
        );
      }

      if (convertedWeight !== undefined && !Number.isNaN(convertedWeight)) {
        setFieldValue("weight", convertedWeight.toString());
      }
    } catch (error) {
      // Safe fallback: If conversion fails, leave the user's original input value unchanged.
    } finally {
      prevWeightUnitRef.current = currentWeightUnit;
      setTimeout(() => {
        isConvertingWeightRef.current = false;
      }, 100);
    }
  }, [values.weightUnits, values.weight, setFieldValue]);

  let barcodeError = isBarcodeError(props.validationError);

  if (hasBarcodeValidationError(barcodeError, props)) {
    errorMessage = {
      [props.name]: props.validationError,
    };
  }

  const errorArray = errors;
  const combinedErrors = {
    ...errorArray,
    ...errorMessage,
  };

  const handleBlur = (e) => {
    if (props.onBlur) {
      props.onBlur(e);
    }
  };

  const handleChange = (e) => {
    if (props.readOnly || props.disabled) return;
    if (!props.readOnly && !props.disabled) {
      const newValue = e.target.value;
      setInputValue(newValue);
      if (props.type === "password") {
        setPassword(newValue);
      }
      if (props.onChange) {
        props.onChange(e);
      }
    }
  };

  const getRawValue = () => {
    if (props.name === "secondary_barcode") {
      return localStorage.getItem("secondary_barcode_id") || "";
    }
    // else if (props.name === "primary_contact_no") {
    //   const raw = values[props.name] || "";
    //   return raw.length >= 3 ? raw : "+44";
    // }
    else if (props.name === "nhs_results") {
      return stripHTML(values[props.name]) || "";
    } else {
      return inputValue || values[props.name] || "";
    }
  };

  const handleFocus = (e) => {
    const value = e.target.value;
    const firstUnderscore = value.indexOf("_");
    const pos = firstUnderscore === -1 ? value.length : firstUnderscore;
    requestAnimationFrame(() => {
      e.target.setSelectionRange(pos, pos);
    });
  };

  const handleClick = (e) => {
    const value = e.target.value;
    const firstUnderscore = value.indexOf("_");
    const newPos = firstUnderscore === -1 ? value.length : firstUnderscore;
    requestAnimationFrame(() => {
      e.target.setSelectionRange(newPos, newPos);
    });
  };

  const handleKeyDown = (e) => {
    const { key, target } = e;
    const value = target.value;
    const firstUnderscore = value.indexOf("_");
    const isComplete = firstUnderscore === -1;

    if (["ArrowLeft", "ArrowRight", "Home", "End"].includes(key)) {
      e.preventDefault();
      const pos = isComplete ? value.length : firstUnderscore;
      requestAnimationFrame(() => {
        target.setSelectionRange(pos, pos);
      });
    }
  };

  const handleBlurEvent = (e) => {
    const cleanedValue = stripHTML(e.target.value);
    handleChangeWithContext(
      {
        ...e,
        target: { ...e.target, value: cleanedValue },
      },
      props,
      setInputValue,
      setFieldValue,
      handleTrackingNumberChange,
      auth,
    );
    if (props.onBlur) props.onBlur(e);
  };

  const handleChangeEvent = (e) => {
    let value = e.target.value;
    const { name } = props;

    if (name === "code") {
      const firstChar = value[0]?.toLowerCase();
      const secondChar = value[1]?.toLowerCase();

      const startsWith = (a, b) => firstChar === a && secondChar === b;
      const isMPR = ["m", "p", "r"].includes(firstChar);

      if (startsWith("c", "o") || startsWith("b", "t")) {
        setMask("aaaaa99999");
      } else if (firstChar === "b") {
        setMask(secondChar === "g" ? "aaa-aaa-9999" : "**********");
      } else if (isMPR) {
        setMask("aaa-aaa-9999");
      } else {
        setMask("**********");
      }
    }

    // if (name === "primary_contact_no" || name === "phone_number_primary") {
    //   value = value.replace(/\D/g, "");
    // }
    handleChangeWithContext(
      {
        ...e,
        target: { ...e.target, value },
      },
      props,
      setInputValue,
      setFieldValue,
      handleTrackingNumberChange,
      auth,
    );
  };

  // Extracted from inline IIFE to reduce cognitive complexity
  const renderNhsIcon = () => {
    if (isNhsNumber)
      return <ExclamationCircleFill className="m-1 fs-6" color={darkGreen} />;
    if (isNhsResult)
      return <InfoCircleFill className="m-1 fs-6" color={darkGreen} />;
    return null;
  };

  const renderLabel = () => (
    <>
      {!props.noLabel && props.label}
      {props.required && props.label !== "" && (
        <span className="required">
          {props.className.includes(["intentional-required"]) ? "*" : ""}
        </span>
      )}
      {renderNhsIcon()}
    </>
  );

  // Extracted from inline IIFE to reduce cognitive complexity
  const renderGroupCheckbox = () => {
    const isGroupCheckbox = GROUP_CHECKBOX_NAMES.has(props.name);
    const isChecked = isGroupCheckbox
      ? getGroupCheckboxChecked(values, props.name, props.label)
      : values[props.name] === true;
    const checkboxLabel = props.customLabel || props.label;

    return (
      <Form.Check
        key={props.controlId}
        type="checkbox"
        id={props.controlId}
        label={checkboxLabel}
        name={props?.name}
        value={props.label}
        onChange={(e) => {
          if (props?.onChange) {
            props.onChange(e);
          }
        }}
        checked={isChecked}
        className={props.className}
        aria-describedby={props.aria_describedby}
      />
    );
  };

  if (isNhsNumber && !values["nhs_number"]) return null;

  const getColumnClass = () => {
    if (props.group_name === "unprotected_sex_type") return "col-md-4";
    if (props.group_name === "potential_sti_exposure_type") return "col-md-3";
    return "";
  };

  const commonProps = {
    id: props.controlId || props.name,
    type: props.type,
    name: props.name,
    as: props.as || Form.Control,
    className: props.className,
    required: props.required,
    placeholder: props.placeholder,
    size: props.size,
    readOnly: props.readOnly,
    disabled: props.disabled,
    min: props.min,
    max: props.max,
    maxLength: props.maxLength,
    "aria-describedby": props.aria_describedby,
    isInvalid:
      props.validationError ||
      (props.touched && !!props.error) ||
      !!(touched[props.name] && combinedErrors[props.name]),
    onChange: handleChangeEvent,
    onBlur: handleBlurEvent,
    value: getRawValue(),
  };

  return (
    !props.hide && (
      <Form.Group
        className={`form-group d-flex flex-column ${getColumnClass()}`}
        id={props.controlId || props.name}
      >
        {props.image && isSafeImageSrc(props.image) && (
          <div>
            <img
              src={props.image}
              alt="Field visual aid"
              className={"form-image mb-2" + props.imgClassname}
            />
          </div>
        )}
        {props.label && (
          <Form.Label className="labels">{renderLabel()}</Form.Label>
        )}
        {props.helpTextLabel && (
          <Form.Text id={props.aria_describedby} muted={true}>
            <span className="text-muted d-inline-block mb-1">
              {props.helpTextLabel}
            </span>
          </Form.Text>
        )}
        {props.type === "checkbox" &&
          props.name !== "consent_steps" &&
          renderGroupCheckbox()}
        {props.type === "radio" && (
          <div className="d-flex flex-wrap">
            {props.choices.map((choice) => (
              <Form.Check
                key={choice.label}
                type="radio"
                id={`${props.name}-${choice.value}`}
                name={props.name}
                value={choice.value}
                label={choice.label}
                checked={props.value === choice.value}
                onChange={handleChange}
                className="register-radio mb-0 col-md-4"
                disabled={props.disabled}
              />
            ))}
            {errors[props.name] &&
              touched[props.name] &&
              props.controlId !== "lifestyleId" &&
              props.controlId !== "Ethnicity-personal-details" &&
              props.name !== "sexually_active" && (
                <div className="invalid-feedback d-block">
                  {errors[props.name]}
                </div>
              )}
          </div>
        )}

        {props.type === "checkbox" && props.name === "consent_steps" && (
          <Form.Check
            type="checkbox"
            id={`default-${props.id}`}
            label={
              <>
                <strong>{props.heading}</strong>
                <br />
                {props.message && (
                  <small className="form-text text-muted">
                    {props.message}
                  </small>
                )}
              </>
            }
            checked={props.checked}
            onChange={props.onChange}
          />
        )}
        {props.type === "select" && props?.name === "home_street_address" && (
          <Select
            aria-label="Default select example"
            placeholder={props.placeholder}
            classNamePrefix="react-select"
            isClearable
            isDisabled={props?.disabled}
            formatOptionLabel={formatAddressOptionLabel}
            filterOption={(option, inputValue) => {
              // Show option if it matches the search
              if (
                option.label.toLowerCase().includes(inputValue.toLowerCase())
              ) {
                return true;
              }
              // If input doesn't match any option, show "Other"
              if (option.value === "No results") {
                const hasMatch = props?.options?.some((opt) =>
                  (opt?.label ?? opt)
                    .toLowerCase()
                    .includes(inputValue.toLowerCase()),
                );
                return !hasMatch && inputValue.length > 0;
              }
              return false;
            }}
            options={[
              ...(props?.options?.map((option) => ({
                value: option?.value ?? option,
                label: option?.label ?? option,
              })) || []),
              { value: "No results", label: "Enter address manually" },
            ]}
            value={((selectedOption) => {
              if (!props.value) return null;
              const matchingOption = props?.options?.find(
                (opt) => (opt?.value ?? opt) === props.value,
              );
              if (props.value !== "" && props.value !== "No results") {
                return { value: props.value, label: props.value };
              }
              if (!matchingOption) {
                return {
                  value: "No results",
                  label: "Enter address manually",
                };
              }
              return {
                value: matchingOption?.value ?? matchingOption,
                label: matchingOption?.label ?? matchingOption,
              };
            })()}
            onChange={(selectedOption) => {
              handleChange({
                target: {
                  name: props.name,
                  value: selectedOption ? selectedOption.value : "",
                },
              });
              props?.handleChange?.(selectedOption);
            }}
            className={props.touched && props.error ? "is-invalid" : ""}
          />
        )}

        {props.type === "select" && props?.name !== "home_street_address" && (
          <Form.Select
            aria-label="Default select example"
            placeholder={props.placeholder}
            value={props?.value}
            isInvalid={props.touched && !!props.error}
            {...props}
            disabled={props?.disabled || props?.readOnly}
            onChange={handleChange}
          >
            <option value="" hidden={props.hidePlaceholderOption}>
              {renderHomeAddressOption(props)}
            </option>
            {props?.options?.map((option, index) => {
              let optionValue;
              let optionLabel;

              if (option?.value || option?.label) {
                optionValue = option?.value || option;
                optionLabel = option?.label || option;
              } else {
                optionValue = option;
                optionLabel = option;
              }
              return (
                <option key={optionLabel} value={optionValue}>
                  {optionLabel}
                </option>
              );
            })}
          </Form.Select>
        )}

        {props.type === "toggle-buttons" && (
          <div className="form-group toggle-btn-group">
            <ToggleButtons
              field={props}
              toggleValueProp={props.toggleValueProp}
              onChange={(e) => {
                if (props?.handleChange) {
                  props?.handleChange?.(e, setFieldValue);
                }
                handleChange(e);
              }}
              noLabel={true}
              setFieldValue={setFieldValue}
              errors={errors}
              touched={touched}
            />
          </div>
        )}

        {![
          "password",
          "radio",
          "checkbox",
          "select",
          "toggle-buttons",
        ].includes(props.type) &&
          (() => {
            if (props.name === "code") {
              const { disabled, ...safeCommonProps } = commonProps;
              return (
                <InputMask
                  mask={mask}
                  maskChar="_"
                  value={getRawValue()}
                  onChange={handleChangeEvent}
                  onBlur={handleBlurEvent}
                  onFocus={handleFocus}
                  onClick={handleClick}
                  onKeyDown={handleKeyDown}
                >
                  {(inputProps) => (
                    <Form.Control {...inputProps} {...safeCommonProps} />
                  )}
                </InputMask>
              );
            }
            if (props.type === "date") {
              return (
                <BasicDatePicker
                  props={props}
                  setFieldValue={setFieldValue}
                  handleChangeEvent={handleChange}
                />
              );
            }
            if (props.type === "time") {
              return (
                <BasicTimePicker
                  props={props}
                  setFieldValue={setFieldValue}
                  handleChangeEvent={handleChangeEvent}
                  handleChange={handleChange}
                />
              );
            }
            if (props.name === "verifyOtp") {
              return (
                <Form.Control
                  {...commonProps}
                  onKeyDown={(e) => {
                    if (props.type === "number") {
                      const allowedKeys = [
                        "Backspace",
                        "Delete",
                        "Tab",
                        "ArrowLeft",
                        "ArrowRight",
                        "Home",
                        "End",
                      ];

                      if (!/^\d$/.test(e.key) && !allowedKeys.includes(e.key)) {
                        e.preventDefault();
                      }
                    }

                    props.onKeyDown?.(e);
                  }}
                  onPaste={(e) => {
                    if (props.type === "number") {
                      const pastedText = e.clipboardData.getData("text");

                      if (!/^\d+$/.test(pastedText)) {
                        e.preventDefault();
                      }
                    }

                    props.onPaste?.(e);
                  }}
                  onChange={(e) => {
                    if (props.type === "number") {
                      e.target.value = e.target.value
                        .replace(/\D/g, "")
                        .slice(0, 6);
                    }

                    if (props?.handleChange) {
                      props.handleChange(e, setFieldValue);
                    }

                    handleChange(e);
                    handleChangeEvent(e);
                  }}
                />
              );
            }
            if (props.type === "phone") {
              return (
                <div className="mb-3">
                  <InputGroup className="phone-number">
                    <InputGroup.Text>+44</InputGroup.Text>
                    <Form.Control
                      {...commonProps}
                      inputMode="numeric"
                      maxLength={PHONE_MAX_LENGTH}
                      onKeyDown={(e) => {
                        const isShortcut = e.ctrlKey || e.metaKey;
                        if (
                          !isShortcut &&
                          e.key.length === 1 &&
                          !/^\d$/.test(e.key)
                        ) {
                          e.preventDefault();
                        }
                      }}
                      onChange={(e) => {
                        // Digits only (also covers paste / autofill)
                        e.target.value = e.target.value
                          .replace(/\D/g, "")
                          .slice(0, PHONE_MAX_LENGTH);
                        if (props?.handleChange) {
                          props?.handleChange?.(e, setFieldValue);
                        }
                        handleChange(e);
                        handleChangeEvent(e);
                      }}
                    />
                  </InputGroup>
                  {(combinedErrors[props.name] || errors[props.name]) &&
                    !props.group_name && (
                      <Form.Control.Feedback type="invalid" className="d-block">
                        {combinedErrors[props.name] || errors[props.name]}
                      </Form.Control.Feedback>
                    )}
                </div>
              );
            }
            if (props.type === "advancePhoneNumber") {
              return (
                <div className="mb-3 w-100">
                  <IntlTelInput
                    {...commonProps}
                    ref={itiRef}
                    inputProps={{
                      id: "phone",
                      ...commonProps,
                    }}
                    initialCountryLookup={initialCountryLookup}
                    excludeCountries={["gb", "gg", "im", "je"]}
                    loadUtils={() => import("intl-tel-input/utils")}
                    onChangeCountry={(iso2) => {
                      const countries = intlTelInput.getAllCountries();
                      const country = countries.find(
                        (country) => country.iso2 === iso2,
                      );
                      const countryCode = country?.dialCode
                        ? `+${country.dialCode}`
                        : "";
                      setFieldValue(`${props.name}_country`, countryCode);
                      setFieldValue(`${props.name}_iso2`, iso2 || "");
                    }}
                    onChangeNumber={(number) => {
                      if (props?.clearOnNonUkSwitch && !auth?.isNonUKNumber) return;
                      setFieldValue(props.name, number);
                    }}
                  />
                  <a></a>
                  {(combinedErrors[props.name] || errors[props.name]) &&
                  !props.group_name &&
                  !props.className?.includes("account41") ? (
                    <Form.Control.Feedback type="invalid" className="d-block">
                      {combinedErrors[props.name] || errors[props.name]}
                    </Form.Control.Feedback>
                  ) : (
                    ""
                  )}
                </div>
              );
            }
            return (
              <Form.Control
                {...commonProps}
                onChange={(e) => {
                  if (props?.handleChange) {
                    props?.handleChange?.(e, setFieldValue); // setFieldValue IS in scope here
                  }
                  handleChange(e);
                  handleChangeEvent(e);
                }}
              />
            );
          })()}

        {props.type === "password" && (
          <InputGroup className="password-container position-relative">
            <Form.Control
              aria-describedby={props.aria_describedby}
              aria-labelledby={props.htmlFor || props.label}
              aria-label={props.htmlFor || props.label}
              role={props.role || "input"}
              className="password-field"
              data-testid={props?.testId}
              as={props?.as || Form.Control}
              disabled={props?.disabled}
              isInvalid={props.touched && !!props.error}
              name={props.name}
              onBlur={handleBlur}
              onChange={handleChange}
              placeholder={props.placeholder}
              readOnly={props.readOnly}
              required={props.required}
              size={props.size}
              type={showPassword ? "text" : "password"}
              value={password || ""}
              maxLength={props.maxLength}
            />
            <button
              type="button"
              className={`eye-toggle ${
                props.touched && !!props.error ? "error-toggle" : ""
              }`}
              onClick={togglePasswordVisibility}
            >
              {showPassword ? (
                <Eye />
              ) : (
                <img
                  src={eyeSlash}
                  alt="hide password"
                  style={{ width: "19px" }}
                />
              )}
            </button>
            {(combinedErrors[props.name] || errors[props.name]) &&
              !props.group_name && (
                <Form.Control.Feedback type="invalid">
                  {combinedErrors[props.name] || errors[props.name]}
                </Form.Control.Feedback>
              )}
          </InputGroup>
        )}

        {props.type === "recaptcha" && (
          <Recaptcha
            className={props.recaptchaClassName}
            onChange={props.recaptchaOnChange}
            elementId={props.recaptchaElementId}
            errors={errors[props.name]}
            touched={touched}
            isInvalid={
              props.validationError ||
              (touched[props.name] && !!errors[props.name])
            }
          />
        )}
        {props.type === "advancePhoneNumber" ||
        props.type === "phone" ||
        props?.identity === "mfaEmail" ? (
          <span
            className="anchor-text"
            onClick={() => {
              if (props.disabled && props?.identity !== "mfaEmail" && !props?.allowNumberTypeSwitch) return;
              const switchingToUK = auth?.isNonUKNumber;
              let currentValue = values[props.name] || "";
              const savedUkNumberKey = `${props.name}_savedUkNumber`;
              const savedNumber =
                typeof auth?.primaryPhoneNumber === "string"
                  ? auth.primaryPhoneNumber
                  : "";
              const savedIsNonUk =
                props?.savedNumberIsNonUk &&
                !savedNumber.replace(/\s+/g, "").startsWith("+44");
              if (switchingToUK) {
                if (props?.clearOnNonUkSwitch) {
                  currentValue =
                    status?.[savedUkNumberKey] ||
                    (savedIsNonUk ? "" : savedNumber);
                }
                currentValue = removeCountryCodeForUkNumber(currentValue);
                setFieldValue(props.name, currentValue);
                setFieldValue(`${props.name}_country`, "+44");
                setFieldValue(`${props.name}_iso2`, "");
              } else {
                let nextValue =
                  typeof auth?.primaryPhoneNumber === "boolean"
                    ? ""
                    : String(auth?.primaryPhoneNumber ?? "");
                if (props?.clearOnNonUkSwitch) {
                  setStatus({ ...status, [savedUkNumberKey]: currentValue });
                  nextValue = savedIsNonUk ? savedNumber : "";
                }
                const detected = getCountryFromPhone(
                  props?.clearOnNonUkSwitch ? nextValue : currentValue,
                );
                setFieldValue(props.name, nextValue);
                setFieldValue(
                  `${props.name}_country`,
                  detected?.code
                    ? `+${detected.code}`
                    : auth.countrySelected || "",
                );
                setFieldValue("emailMFA", false);
              }
              auth.setIsNonUKNumber(!auth?.isNonUKNumber);
            }}
            style={{
              cursor:
                props.disabled && props?.identity !== "mfaEmail" && !props?.allowNumberTypeSwitch
                  ? "default"
                  : "pointer",
              display: "inline-block",
              width: "fit-content",
              alignSelf: "flex-start",
            }}
          >
            {auth.isNonUKNumber === true
              ? "Use a UK number instead"
              : "I don't have a UK number"}
          </span>
        ) : (
          ""
        )}
        {(combinedErrors[props.name] || errors[props.name]) &&
          !props.group_name && (
            <Form.Control.Feedback type="invalid">
              {formatErrorMessage(
                combinedErrors[props.name] || errors[props.name],
              )}
            </Form.Control.Feedback>
          )}

        {props.helpText && (
          <Form.Text
            className={`${props.helpTextClass} help-text d-flex flex-column ${
              props.className === "helptext" && "create-account-helptext"
            }`}
            id={props.aria_describedby}
            muted={props.muted}
          >
            {props.helpTextClass === "required" && (
              <span className="required helptext-required">⋆</span>
            )}
            {props.helpText === "I don't have a UK number" ? (
              ""
            ) : (
              <ReactMarkdown>
                {`${props.helpText}${
                  props.type === "advancePhoneNumber"
                    ? "\n\nPlease note that our medical team may not be able to call all non-UK phone numbers directly."
                    : ""
                }`}
              </ReactMarkdown>
            )}
          </Form.Text>
        )}

        {props.helpTextAbove && (
          <Form.Text id={props.aria_describedby} muted={true}>
            <span className="text-muted">{props.helpTextAbove}</span>
          </Form.Text>
        )}

        <div>
          {props.links && (
            <span className="d-flex justify-content-start">{props.links}</span>
          )}
        </div>
      </Form.Group>
    )
  );
}

FormInput.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  validationError: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  hide: PropTypes.bool,
  controlId: PropTypes.string,
  image: PropTypes.string,
  imgClassname: PropTypes.string,
  required: PropTypes.bool,
  noLabel: PropTypes.bool,
  label: PropTypes.string,
  type: PropTypes.string,
  choices: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    }),
  ),
  id: PropTypes.string,
  htmlFor: PropTypes.string,
  checked: PropTypes.bool,
  heading: PropTypes.string,
  message: PropTypes.string,
  testId: PropTypes.string,
  readOnly: PropTypes.bool,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  size: PropTypes.string,
  min: PropTypes.number,
  max: PropTypes.number,
  maxLength: PropTypes.number,
  aria_describedby: PropTypes.string,
  role: PropTypes.string,
  as: PropTypes.elementType,
  touched: PropTypes.bool,
  error: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        value: PropTypes.string,
        label: PropTypes.string,
      }),
    ]),
  ),
  toggleValueProp: PropTypes.string,
  helpText: PropTypes.string,
  helpTextAbove: PropTypes.string,
  helpTextLabel: PropTypes.string,
  className: PropTypes.string,
  helpTextClass: PropTypes.string,
  muted: PropTypes.bool,
  recaptchaClassName: PropTypes.string,
  recaptchaOnChange: PropTypes.func,
  recaptchaElementId: PropTypes.string,
  group_name: PropTypes.string,
  values: PropTypes.object,
  links: PropTypes.any,
  onInformedConsentClick: PropTypes.func,
  handleChange: PropTypes.func,
  customLabel: PropTypes.string,
};

export default FormInput;
